import namesEn from './country-names.en.json';
import phones from './country-phone.json';

/**
 * Label overrides applied on top of ISO/UN M.49 names in country-names.en.json.
 * Keep overrides minimal and documented — do not add more without product approval.
 */
const LABEL_OVERRIDES: Partial<Record<string, string>> = {
  // Only intentional drift from ISO/UN M.49 naming; DQS requested Taiwan without Province of China
  TW: 'Taiwan',
};

const BASE_NAMES: Record<string, string> = { ...namesEn };

for (const [code, label] of Object.entries(LABEL_OVERRIDES)) {
  if (label) {
    BASE_NAMES[code] = label;
  }
}

export type CountryCatalogEntry = {
  code: string;
  name: string;
  phone?: string;
};

export function normalizeCountryCode(code: string | null | undefined): string | undefined {
  if (!code) return undefined;
  const trimmed = code.trim().toUpperCase();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function getCountryName(
  code: string | null | undefined,
  fallback = '',
): string {
  const normalized = normalizeCountryCode(code);
  if (!normalized) return fallback;
  return BASE_NAMES[normalized] ?? (fallback || normalized);
}

export function getCountryPhone(code: string | null | undefined): string | undefined {
  const normalized = normalizeCountryCode(code);
  if (!normalized) return undefined;
  const phone = (phones as Record<string, string>)[normalized];
  return phone ?? undefined;
}

export function listCountryCatalog(): CountryCatalogEntry[] {
  return Object.keys(BASE_NAMES)
    .sort()
    .map((code) => ({
      code,
      name: BASE_NAMES[code],
      phone: getCountryPhone(code),
    }));
}

export function getCountryNamesMap(): Readonly<Record<string, string>> {
  return BASE_NAMES;
}

/**
 * Map a free-text search string to ISO codes whose code or catalog label matches.
 * Use the returned codes in SQL (`WHERE country_code IN (...)`), not DB name columns.
 */
export function findCountryCodesMatching(
  search: string | null | undefined,
): string[] {
  if (!search?.trim()) return [];
  const term = search.trim().toLowerCase();
  return Object.entries(BASE_NAMES)
    .filter(
      ([code, name]) =>
        code.toLowerCase().includes(term) || name.toLowerCase().includes(term),
    )
    .map(([code]) => code);
}

/** Resolve a stored value that may be an ISO code or a catalog display name. */
export function resolveCountryCode(
  value: string | null | undefined,
): string | undefined {
  const asCode = normalizeCountryCode(value);
  if (asCode && BASE_NAMES[asCode]) return asCode;
  if (!value?.trim()) return undefined;
  const lower = value.trim().toLowerCase();
  const byName = Object.entries(BASE_NAMES).find(
    ([, name]) => name.toLowerCase() === lower,
  );
  return byName?.[0];
}
